import { createApi } from '@reduxjs/toolkit/query/react'

import { FeatureType } from 'clickNgo/configs/feature'
import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import {
  OnboardingContactServiceMethods,
  OnboardingFail,
  OnboardingResponse,
  OnboardingResponseStatus,
  OnboardingServices,
} from 'clickNgo/configs/service/onboarding'
import { WebSocketStoreKeys } from 'clickNgo/configs/service/ws'
import {
  axiosProcedureQuery,
  getJSendErrorMessages,
  transformToProcedurePayload,
} from 'clickNgo/helpers/api'
import { getAccessToken } from 'cng-common/helpers/access'
import { NotificationController } from 'core-components/library/popup'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import Logger from 'core/utils/Logger'

import { emptyContactFlags } from '../config/contactFlags'
import { ContactFlagRequestAction, IContactFlag, IContactFlags } from '../model/contactFlag'

import { mapContactFlagFromContactFlagsAllData } from './mapping'

export enum ContactFlagApiTags {
  ContactFlagsByContactIds = 'ContactFlagsByContactIds',
  UpdateContactFlags = 'UpdateContactFlags',
}

type GetContactFlagsByContactIdsParams = {
  contactIds: string[]
  topPartnerId: string
}

type GetContactFlagsByContactIdParams = {
  contactId: string
  topPartnerId: string
}

type GetContactFlagsByContactIdsResponse = IContactFlag[]

type GetContactFlagsByContactIdResponse = IContactFlags

type UpdateContactFlagsParams = {
  contactFlags: IContactFlag[]
  action: ContactFlagRequestAction
  // ProfilingRequestSource.ClickAndGoPortal by default
  source: ProfilingRequestSource
}

type UpdateContactFlagsResponse = unknown

export const contactFlagApi = createApi({
  reducerPath: `${FeatureType.CngApi}/contactFlagApi`,
  baseQuery: axiosProcedureQuery<any[]>(),
  tagTypes: Object.values(ContactFlagApiTags),
  endpoints(build) {
    return {
      /**
       * Get contact flags by contact IDs
       * This query retrieves the flags for a list of contacts.
       */
      getContactFlagsByContactIds: build.query<
        GetContactFlagsByContactIdsResponse,
        GetContactFlagsByContactIdsParams
      >({
        transformResponse(response) {
          return response.Data.map(mapContactFlagFromContactFlagsAllData)
        },

        providesTags: [{ type: ContactFlagApiTags.ContactFlagsByContactIds, id: 'LIST' }],

        query(params) {
          return {
            url: `/webservice/197/sp/v4/spGetContactFlagsData/call`,
            method: 'POST',
            data: {
              ...transformToProcedurePayload(params),
            },
          }
        },
      }),

      // Get contact flags by contact ID
      getContactFlagsByContactId: build.query<
        GetContactFlagsByContactIdResponse,
        GetContactFlagsByContactIdParams
      >({
        transformResponse(response) {
          return response.Data.map(mapContactFlagFromContactFlagsAllData).reduce<IContactFlags>(
            (acc, item) => {
              acc[item.type].push(item)
              return acc
            },
            emptyContactFlags
          )
        },

        query(params) {
          return {
            url: `/webservice/197/sp/v4/spGetContactFlagsData/call`,
            method: 'POST',
            data: {
              ...transformToProcedurePayload({
                contactIds: [params.contactId],
                topPartnerId: params.topPartnerId,
              }),
            },
          }
        },
      }),

      /**
       * Update contact flags
       * This mutation updates the flags for a list of contacts.
       */
      updateContactFlags: build.mutation<UpdateContactFlagsResponse, UpdateContactFlagsParams>({
        queryFn: async ({ contactFlags, action, source }) => {
          const token = getAccessToken()
          const wsClient = WebSocketStore.get(WebSocketStoreKeys.Onboarding)

          const payload = {
            contactFlags: contactFlags.map((flag) => ({
              ...flag,
              source,
              action,
            })),
          }

          const response = (await wsClient.call({
            service: OnboardingServices.Contact,
            action: OnboardingContactServiceMethods.UpdateFlags,
            payload,
            token: token || undefined,
            withoutTokenRefresh: true,
            isAuth: true,
            returnError: true,
          })) as OnboardingResponse<OnboardingFail, unknown>

          if (response.status === OnboardingResponseStatus.Error) {
            const { message, status } = response
            NotificationController.showError({ message })
            Logger.error({ message, status })

            return {
              error: { message, status },
            }
          }

          if (response.status === OnboardingResponseStatus.Fail) {
            const message = getJSendErrorMessages(response).join(', ')
            const status = response.status
            NotificationController.showError({ message })
            Logger.error({ message, status })
            return {
              error: { message, status },
            }
          }

          return { data: response.data }
        },
      }),
    }
  },
})

export const {
  useGetContactFlagsByContactIdQuery,
  useGetContactFlagsByContactIdsQuery,
  useLazyGetContactFlagsByContactIdsQuery,
  useUpdateContactFlagsMutation,
} = contactFlagApi
